
import React, {createRef, useCallback, useEffect, useMemo, useState} from 'react';
import {
    SelectedItemRequiredKeys,
    SelectedItemRequiredKeysInterface
} from "src/interfaces/domain-tracker";
import Button from "@amzn/awsui-components-react/polaris/button";
import {convertArrayToTableSelect} from "src/utils/table-helper";
import Modal from "@amzn/awsui-components-react/polaris/modal";
import Select from "@amzn/awsui-components-react/polaris/select";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import {UpdateDelayAlert} from "src/components/common/user-alerts"
import {SWO_ORCHESTRATOR_SERVICE_CLIENT_ID} from "src/constants/domain-tracker";
import {DomainTracker} from "src/model/DomainTracker";
import {v1 as uuidv1} from 'uuid';
import {getUserAlias} from "src/utils/cookie-helper";
import {TableProgressBar, useProgressBar} from "src/components/common/table-progress-bar";
import {RestartRecipeExecutionResult} from "@amzn/sm-workflow-orchestration-service-js-client/lib/smworkfloworchestrationservicelambda";
import {ExecutionRestartInputOverride} from "src/components/domain-tracker/execution-restart-input-override";
import {SwoUiMetrics} from "src/utils/swo-ui-metrics/swo-ui-metrics";
import {metricFeaturesList, metricPageNames} from "src/constants/swo-ui-metric-constants";
import {logger} from "src/utils/logger";

const NULL_VALUE_OPTION: Select.Option = {id: "PLEASE_SELECT", label: "Please select"};

/**
 * Redrives execution with option to select stage
 * @param props
 * @constructor
 */
export function ExecutionRedriveModal(props: {selectedItems: Array<SelectedItemRequiredKeysInterface>}) {
    const [selectedItems, setSelectedItems] = useState<Array<{[k in SelectedItemRequiredKeys]: string}>>(props.selectedItems);
    const [showRedriveModal, setShowRedriveModal] = useState<boolean>(false);
    const [bulkUpdatePromises, setBulkUpdatePromises] = useState<Array<Promise<any>>>([])
    const [progressBarVisible, setProgressBarVisible] = useState<boolean>(false)
    const submitFormRef: any = createRef();
    const toastSuccessMessage: React.ReactNode = <>Redriven successfully. <UpdateDelayAlert feature={"Workflow status"}
                                                                                             plainText={true}/></>;
    const {
        progressPercentage,
        progressLoading,
        progressStatus,
        isCompleted,
        isCompletedSuccessfully,
        errorMessages
    } = useProgressBar(bulkUpdatePromises, toastSuccessMessage);

    const defaultRedriveOptions = useMemo(() => [...convertArrayToTableSelect([], true, NULL_VALUE_OPTION)], []);

    const [redriveDropdownOptions, setRedriveDropdownOptions] = useState<Array<Select.IOption | Select.Option | Select.OptionsGroup>>(defaultRedriveOptions)

    const [
        selectedOption,
        setSelectedOption
    ] = useState<Select.Option>(redriveDropdownOptions[0] as Select.Option);

    useEffect(() => {
        setSelectedItems(props.selectedItems)
        setProgressBarVisible(false)
        setSelectedOption(NULL_VALUE_OPTION)
    }, [props.selectedItems, showRedriveModal])

    useEffect(() => {
        if (isCompletedSuccessfully) {
            setShowRedriveModal(false)
        }
    }, [isCompletedSuccessfully])


    function handleRedriveSubmitButton(inputOverrideData: JSON, enableConfigOverride: boolean): void {
        logger.info(`Redriving ${selectedItems.length} workflow instances`, props.selectedItems.map(selectedItem => selectedItem.workflowId))
        SwoUiMetrics.publishCloudWatchCountMetric(
            props.selectedItems.length > 1 ? metricFeaturesList.bulkRedriveWorkflowInstance : metricFeaturesList.redriveWorkflowInstance,
            metricPageNames.redriveWorkflowInstance
        )
        const domainTracker = new DomainTracker();
        setProgressBarVisible(true);
        const redrivePluginName: string = selectedOption.id;
        const hasInputOverrideData = Object.keys(inputOverrideData).length > 0 ;
        const redriveExecutionInput = enableConfigOverride && hasInputOverrideData ? JSON.stringify(inputOverrideData) : undefined;
        const redriveRequestPromises: Array<Promise<RestartRecipeExecutionResult>> = selectedItems.map(selectedItem => {
            const lastAttemptId: string = selectedItem.recipeExecutionAttemptId || selectedItem.workflowId
            return domainTracker.redriveExecution(SWO_ORCHESTRATOR_SERVICE_CLIENT_ID, selectedItem.workflowId, lastAttemptId,
                uuidv1(), getUserAlias(), redrivePluginName, redriveExecutionInput)
        })
        setBulkUpdatePromises(redriveRequestPromises);
    }

    const updateRedriveOptions = useCallback((redriveStages: Array<Select.IOption | Select.Option | Select.OptionsGroup>) => {
        setRedriveDropdownOptions([...defaultRedriveOptions, ...redriveStages]);
    }, [defaultRedriveOptions])

    const closeRedriveDialog = useCallback(() => {
        setShowRedriveModal(false)
    }, [])

    return <>
        <Modal
            className={"col-xxs-12 awsui-util-mb-l"}
            id={"redrive-execution-modal"}
            onDismiss={() => setShowRedriveModal(false)}
            visible={showRedriveModal}
            expandToFit={true}
            size="medium"
            header={`Redrive Execution`}
        >
            <FormField label="Select stage"
                       description="Please select stage from which you want to redrive the execution">
                <Select
                    id={"redrive-execution-select-dropdown"}
                    selectedOption={selectedOption}
                    onChange={({detail}) => {
                        setProgressBarVisible(false)
                        setSelectedOption(detail.selectedOption)
                    }
                    }
                    options={redriveDropdownOptions}
                />
            </FormField>
            <br/>

            <ExecutionRestartInputOverride selectedItems={props.selectedItems}
                                           onRestartSubmitButton={handleRedriveSubmitButton}
                                           modalVisible={showRedriveModal}
                                           submitFormRef={submitFormRef}
                                           setRestartStageOptions={updateRedriveOptions}
                                           closeRestartDialog={closeRedriveDialog}
                                           workflowDetailsLoading={progressLoading}
                                           disableWorkflowRestart={NULL_VALUE_OPTION.id === selectedOption.id}
                                           actionType="redrive"
            > </ExecutionRestartInputOverride>
            {((progressLoading || isCompleted) && progressBarVisible) &&
            <div>
                <TableProgressBar progressStatus={progressStatus}
                                  progressPercentage={progressPercentage}
                                  totalRecordLength={selectedItems.length}
                                  title={`Redriving ${selectedItems.length} execution(s)`}
                                  description={`Action: ${selectedOption.label}`}
                                  errorMessages={errorMessages}
                />
            </div>
            }
        </Modal>
        <Button text={"Redrive"}
                id={"redrive-execution-button"}
                onClick={() => setShowRedriveModal(true)}
                disabled={props.selectedItems.length == 0}
        />

    </>
}
